import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  Box,
  Button,
  Grid,
  Link,
  TextField,
  Theme,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';

import SubHeader from '../../../../components/Header/SubHeader.component';
import Loader from '../../../../components/LoaderSplash/Loader.component';
import SharedDialog from '../../../../components/SharedDialog/SharedDialog.component';
import { appConfig } from '../../../../config/app';
import { CustomEvents } from '../../../../constants/analytics.constant';
import { useEndorsementsDataContext } from '../../../../context/endorsements/endorsements.provider';
import { useVehiclesContext } from '../../../../context/vehicles/vehicles.provider';
import useForm from '../../../../helpers/useForm';
import useLogEventInAnalytics from '../../../../hooks/useLogAnalyticsEvent';
import { useTipification } from '../../../../hooks/useTipification';
import VehicleDetailsValidations from './VehicleDetails.validations';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    itemNames: {
      marginTop: '32px',
      marginBottom: '6px',
    },
  })
);

const VehicleDetails = () => {
  const {
    loading,
    vehicle,
    getVehicleById,
    createEndorsement,
    cleanSuccessErrors,
    errors: vehicleErrors,
    success,
  } = useVehiclesContext();
  const { cleanEndorsementBody } = useEndorsementsDataContext();
  const classes = useStyles();
  const { logEvent } = useLogEventInAnalytics();

  const [open, setOpen] = useState(false);
  const { id } = useParams<any>();
  const history = useHistory();
  const [isEditting, setIsEditting] = useState(false);
  const [defaultValues, setDefaultValues] = useState({
    license_plate: vehicle?.license_plate || '',
    engine_number: vehicle?.engine_number || '',
    chassis_number: vehicle?.chassis_number || '',
  });
  const { trackEvent, events } = useTipification();

  useEffect(() => {
    cleanEndorsementBody();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDefaultValues({
      license_plate: vehicle?.license_plate || '',
      engine_number: vehicle?.engine_number || '',
      chassis_number: vehicle?.chassis_number || '',
    });
  }, [vehicle]);

  const onSubmit = () => {
    if (
      values.license_plate !== vehicle.license_plate ||
      values.chassis_number !== vehicle.chassis_number ||
      values.engine_number !== vehicle.engine_number
    ) {
      setOpen(true);
      setIsEditting(false);
    }
  };

  const onClose = () => {
    setOpen(false);
    cleanSuccessErrors();
  };

  const onCancel = () => {
    setIsEditting(false);
    reset();
  };

  const requestEndorsement = () => {
    setOpen(false);
    const requestValues: any = {};

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        if (values[key] !== vehicle[key]) {
          requestValues[key] = values[key];
        }
      }
    }
    if (requestValues.license_plate)
      trackEvent(events.ENDORSEMENT_PATENT, requestValues.license_plate);
    if (requestValues.chasis_number)
      trackEvent(events.ENDORSEMENT_CHASIS, requestValues.chasis_number);
    if (requestValues.engine_number)
      trackEvent(events.ENDORSEMENT_MOTOR, requestValues.engine_number);
    createEndorsement({
      vehicle_id: vehicle.id,
      ...requestValues,
    });
  };

  const logAnalytics = async () => {
    await logEvent(CustomEvents.OPEN_WHATSAPP, {
      app: appConfig?.NAME,
    });
  };

  const { values, errors, handleChange, handleSubmit, reset } = useForm(
    onSubmit,
    VehicleDetailsValidations,
    defaultValues,
    vehicle
  );

  useEffect(() => {
    if (id) {
      if (vehicle.id !== Number(id)) {
        getVehicleById(id);
      }
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (vehicleErrors.endorsement || success.endorsement) {
      setOpen(true);
      getVehicleById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleErrors, success]);

  return (
    <>
      <Box display={loading ? 'block' : 'none'}>
        <Loader action={'Cargando vehículo'}></Loader>
      </Box>

      {!!Object.keys(vehicle).length && (
        <>
          <SubHeader
            content={
              <>
                <Typography variant="h6" display="inline">
                  {/* <VehicleIcon />{' '} */}
                </Typography>
                <Typography display="inline" variant="h6">
                  {vehicle.brand +
                    ' ' +
                    vehicle.model.split(' ')[0] +
                    ' ' +
                    vehicle.year +
                    ' / ' +
                    vehicle.license_plate}
                </Typography>
              </>
            }
            actions={
              <>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() =>
                    history.push(`/endorsements/process/${vehicle.id}`)
                  }
                >
                  Modificar póliza
                </Button>
              </>
            }
          ></SubHeader>

          <SharedDialog
            size={
              !success.endorsement && !vehicleErrors.endorsement ? 'sm' : 'xs'
            }
            fullWidth={!success.endorsement && !vehicleErrors.endorsement}
            disableBackdropClick={true}
            open={open}
            content={
              <Box>
                <Typography variant="h6">
                  {success.endorsement &&
                    '¡Tu solicitud se registró con éxito!'}
                  {vehicleErrors.endorsement &&
                    '¡Lo sentimos! Ocurrió un error que debemos revisar.'}
                  {!success.endorsement &&
                    !vehicleErrors.endorsement &&
                    '¿Estás seguro de los cambios a solicitar en tu póliza?'}
                </Typography>
                {success.endorsement && (
                  <Grid container>
                    <Grid item xs={12}>
                      La gestionaremos con prioridad en las próximas 72 horas
                      hábiles.
                    </Grid>
                  </Grid>
                )}

                {vehicleErrors.endorsement && (
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography display="inline">
                        Para poder ingresar necesitamos que declares tu patente
                        en la compañía. Podés hacerlo por Whatsapp al
                      </Typography>
                      <Link
                        href={appConfig?.WHATSAPP_LINK}
                        rel="noopener"
                        target="_blank"
                      >
                        <Typography align="left" color="secondary">
                          {' '}
                          <div onClick={() => logAnalytics()}>
                            {appConfig?.FORMATED_WHATSAPP}.
                          </div>
                        </Typography>
                      </Link>
                    </Grid>
                  </Grid>
                )}
                {!success.endorsement && !vehicleErrors.endorsement && (
                  <Grid container>
                    {vehicle?.license_plate !== values.license_plate && (
                      <>
                        <Grid item xs={6}>
                          {' '}
                          Patente:{' '}
                        </Grid>
                        <Grid item xs={6}>
                          {' '}
                          De {vehicle.license_plate} a {values.license_plate}{' '}
                        </Grid>
                      </>
                    )}
                    {vehicle?.chassis_number !== values.chassis_number && (
                      <>
                        <Grid item xs={6}>
                          {' '}
                          N° de chasis:{' '}
                        </Grid>
                        <Grid item xs={6}>
                          {' '}
                          De {vehicle.chassis_number} a {values.chassis_number}{' '}
                        </Grid>
                      </>
                    )}
                    {vehicle?.engine_number !== values.engine_number && (
                      <>
                        <Grid item xs={6}>
                          {' '}
                          N° de motor:{' '}
                        </Grid>
                        <Grid item xs={6}>
                          {' '}
                          De {vehicle.engine_number} a {values.engine_number}{' '}
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </Box>
            }
            onClose={onClose}
            actions={
              success.endorsement || vehicleErrors.endorsement ? (
                <Grid container justify="center" alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Box m={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={onClose}
                      >
                        Aceptar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Grid container>
                  <Grid item xs={12} sm={6}>
                    <Box m={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={requestEndorsement}
                      >
                        Guardar y solicitar cambios
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box m={2}>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={onClose}
                      >
                        Descartar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )
            }
          ></SharedDialog>

          <Grid container justify="center">
            <Grid item xs={11} sm={10} lg={8}>
              {/* VEHCILE INFO */}

              <Grid container>
                <Grid item xs={12}>
                  <Box my={2}>
                    <Typography
                      style={{
                        fontWeight: 'normal',
                        marginTop: '30px',
                        fontSize: '22px',
                      }}
                    >
                      Detalle vehículo
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> Cobertura </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {vehicle.coverage}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> Modelo y versión </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {vehicle.model.split(' ')[0] +
                        ' ' +
                        vehicle.model.split(' ').slice(1).join(' ')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> Año </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {vehicle.year}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={8}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> Asistencia </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      {vehicle.assistance_name}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> Suma asegurada </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                      $ {vehicle.insured_amount.toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> Patente * </Typography>
                  </Box>
                  {isEditting ? (
                    <Box mr={2}>
                      <TextField
                        error={Boolean(errors['license_plate'])}
                        helperText={errors['license_plate']}
                        size="small"
                        fullWidth={true}
                        name="license_plate"
                        id="license_plate"
                        placeholder="Ingresá la patente"
                        variant="outlined"
                        onChange={handleChange}
                        value={values.license_plate}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Typography
                        variant="body1"
                        style={{ fontWeight: 'bold' }}
                      >
                        {vehicle.license_plate}
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> N° de motor * </Typography>
                  </Box>
                  {isEditting ? (
                    <Box mr={2}>
                      <TextField
                        error={Boolean(errors['engine_number'])}
                        helperText={errors['engine_number']}
                        size="small"
                        fullWidth={true}
                        name="engine_number"
                        id="engine_number"
                        placeholder="Ingresá el N° de motor"
                        variant="outlined"
                        onChange={handleChange}
                        value={values.engine_number}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Typography
                        variant="body1"
                        style={{ fontWeight: 'bold' }}
                      >
                        {vehicle.engine_number}
                      </Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box my={2} className={classes.itemNames}>
                    <Typography variant="body2"> N° de chasis * </Typography>
                  </Box>

                  {isEditting ? (
                    <Box mr={2}>
                      <TextField
                        error={Boolean(errors['chassis_number'])}
                        helperText={errors['chassis_number']}
                        size="small"
                        fullWidth={true}
                        name="chassis_number"
                        id="chassis_number"
                        placeholder="Ingresá el N° de chasis"
                        variant="outlined"
                        onChange={handleChange}
                        value={values.chassis_number}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Typography
                        variant="body1"
                        style={{ fontWeight: 'bold' }}
                      >
                        {vehicle.chassis_number}
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {errors['limitChanges'] && (
                  <Grid xs={12}>
                    <Typography color="error">
                      {errors['limitChanges']}
                    </Typography>
                  </Grid>
                )}

                {/* <Box my={4} width={"100%"} display={"flex"} justifyContent={"center"}>
                  <Grid item xs={12} md={6}>
                    {
                      !isEditting && <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={() => setIsEditting(true)}
                        disabled={!vehicle.can_request_endorsement}
                      >
                        Solicitar modificación de los datos
                      </Button>
                    }
                    {
                      !vehicle.can_request_endorsement && "Tu solicitud de actualización de datos está en proceso."
                    }               
                  </Grid>
                </Box> */}
              </Grid>
            </Grid>
          </Grid>

          {/* Esta seccion esta activa? Que hace? */}
          <Grid container justify="center">
            <Grid
              item
              xs={11}
              sm={10}
              md={3}
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              {isEditting && (
                <Box>
                  <Box mb={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleSubmit}
                      disabled={!!Object.keys(errors).length}
                    >
                      Enviar
                    </Button>
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={onCancel}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default VehicleDetails;
